(() => {
  if (window.RogueRedesignLoaded) return;
  window.RogueRedesignLoaded = true;

  // --- Gallery chrome: slide counter, custom page dots, "watch product video" jump button. ---
  // The theme's Flickity gallery already supports prev/next arrows and page dots (see
  // window.theme.Flickity options in product-info.js), but pageDots is hardcoded off for the
  // main slider and there is no counter/video-jump UI in the stock markup, so we add a small,
  // self-contained layer here rather than editing the shared product/media snippets.
  function initGalleryChrome(root) {
    if (root.dataset.rrGalleryInit) return;
    const wrapper = root.querySelector('.product-gallery');
    const slider = wrapper && wrapper.querySelector('[data-product-single-media-slider]');
    if (!wrapper || !slider) return;

    const slides = Array.from(slider.querySelectorAll('[data-product-single-media-wrapper]'));
    if (slides.length < 2) return;
    root.dataset.rrGalleryInit = 'true';

    // Flickity (see product-info.js) treats every direct child of the slider element as a
    // cell — it has no cellSelector option set — so our chrome must live OUTSIDE the slider,
    // in a wrapping column we add around it, not injected into the slider itself.
    const column = document.createElement('div');
    column.className = 'rr-gallery-column';
    slider.insertAdjacentElement('beforebegin', column);
    column.appendChild(slider);

    // Cap the thumbnail column (see .product-single__thumbnails in rr-product-redesign.css) to
    // match this column's real rendered height, in px, via a CSS custom property. Can't do this
    // in pure CSS (align-self:stretch fills .product-gallery's own stretched height — the info
    // column's height, not this one — see the CSS comment), and there's no static px value that
    // holds across viewport widths or per-product image aspect ratios, so it's measured live and
    // re-measured whenever this column's size actually changes (viewport resize, image finishing
    // load, switching between products via the swap-product AJAX flow, etc).
    const thumbsWrap = wrapper.querySelector('.product-single__thumbnails');
    if (thumbsWrap && window.ResizeObserver) {
      const syncThumbsHeight = () => {
        thumbsWrap.style.setProperty('--rr-thumbs-max-height', column.offsetHeight + 'px');
      };
      new ResizeObserver(syncThumbsHeight).observe(column);
      syncThumbsHeight();
    }

    const chrome = document.createElement('div');
    chrome.className = 'rr-gallery-chrome';

    const counter = document.createElement('div');
    counter.className = 'rr-gallery-counter';
    counter.innerHTML = '<span data-rr-gallery-current>1</span>/<span>' + slides.length + '</span>';
    chrome.appendChild(counter);

    // The trigger link lives in a hidden <video-popup> rendered server-side by
    // rr-product-main.liquid (it needs the product's actual video URL, which only Liquid
    // knows) — its own connectedCallback (video-play.js) listens for clicks on
    // [data-video-play] and opens the theme's PhotoSwipe video modal, autoplaying, exactly
    // like snippets/video.liquid does elsewhere in the theme. We just forward our button's
    // click to it rather than reimplementing a lightbox.
    const videoPopupLink = root.querySelector('.rr-gallery-video-trigger [data-rr-video-popup-link]');
    if (videoPopupLink) {
      const videoBtn = document.createElement('button');
      videoBtn.type = 'button';
      videoBtn.className = 'rr-gallery-watch-video';
      videoBtn.innerHTML = '<span class="rr-gallery-watch-video__icon" aria-hidden="true"></span>Watch product video';
      videoBtn.addEventListener('click', () => videoPopupLink.click());
      chrome.appendChild(videoBtn);
    }

    column.appendChild(chrome);

    const dots = document.createElement('div');
    dots.className = 'rr-gallery-dots';
    slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'rr-gallery-dot' + (index === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Go to image ' + (index + 1));
      dot.addEventListener('click', () => selectSlide(index));
      dots.appendChild(dot);
    });
    column.appendChild(dots);

    function selectSlide(index) {
      const flkty = window.theme && window.theme.Flickity && window.theme.Flickity.data(slider);
      if (flkty) flkty.select(index);
    }

    function syncActive(index) {
      counter.querySelector('[data-rr-gallery-current]').textContent = index + 1;
      dots.querySelectorAll('.rr-gallery-dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
      scrollThumbnailIntoView(index);
    }

    // On many-photo products the thumbnail column is capped to the main image's height and
    // scrolls internally (see .product-single__thumbnails in rr-product-redesign.css) instead of
    // growing past it, so the active thumbnail can end up scrolled out of view when navigating via
    // the main image's arrows/dots. Keep it in view (centered) by matching the now-active slide's
    // plain data-id against the thumbnail's own data-id — both are just the raw media id (e.g.
    // "48107312283963"); their *other* id attributes (data-media-id / data-thumbnail-id) look like
    // a matching pair at a glance and are what the theme's own handleMediaFocus() in
    // product-info.js keys off, but actually carry two different "unique" prefixes on this section
    // (one includes the product id, the other doesn't) so they never actually match here — plain
    // data-id is the one attribute confirmed identical on both elements. Not assuming index order
    // matches between the two lists, since the featured media is rendered first in both but that's
    // an implementation detail worth not depending on.
    function scrollThumbnailIntoView(index) {
      const thumbsWrap = root.querySelector('.product-single__thumbnails');
      const activeSlide = slides[index];
      if (!thumbsWrap || !activeSlide) return;
      const mediaId = activeSlide.getAttribute('data-id');
      const thumb = mediaId && thumbsWrap.querySelector('.product-single__thumbnail[data-id="' + mediaId + '"]');
      if (thumb) thumb.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    (function bindFlickity(attemptsLeft) {
      const flkty = window.theme && window.theme.Flickity && window.theme.Flickity.data(slider);
      if (flkty) {
        flkty.on('change', syncActive);
        return;
      }
      if (attemptsLeft > 0) requestAnimationFrame(() => bindFlickity(attemptsLeft - 1));
    })(60);
  }

  function initValuesSlider(section) {
    const grid = section.querySelector('.rr-values__grid');
    if (!grid || grid.dataset.rrSliderInit) return;
    const start = () => {
      if (!window.jQuery || !window.jQuery.fn.slick) {
        setTimeout(start, 50);
        return;
      }
      grid.dataset.rrSliderInit = 'true';
      const wrap = section.querySelector('.rr-values__carousel-wrap');
      window.jQuery(grid).slick({
        infinite: false,
        arrows: true,
        dots: false,
        prevArrow: wrap ? window.jQuery(wrap).find('.rr-values__arrow--prev') : undefined,
        nextArrow: wrap ? window.jQuery(wrap).find('.rr-values__arrow--next') : undefined,
        slidesToShow: 3,
        slidesToScroll: 1,
        responsive: [
          { breakpoint: 1024, settings: { slidesToShow: 2, slidesToScroll: 1 } },
          { breakpoint: 750, settings: { slidesToShow: 1, slidesToScroll: 1 } },
        ],
      });
    };
    start();
  }

  function initHeroVideoSound(section) {
    const videoBackground = section.querySelector('video-background');
    const player = videoBackground && videoBackground.querySelector('[data-video-player]');
    if (!player || videoBackground.dataset.rrVideoControlsInit) return;
    videoBackground.dataset.rrVideoControlsInit = 'true';

    const setup = (video) => {
      video.pause();
      video.muted = true;
      video.removeAttribute('controls');
      player.classList.remove('video__player');
      player.classList.add('rr-hero-video-player');

      const bigPlay = document.createElement('button');
      bigPlay.type = 'button';
      bigPlay.className = 'rr-hero-video-play';
      bigPlay.setAttribute('aria-label', 'Play video');
      bigPlay.innerHTML =
        '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
      videoBackground.appendChild(bigPlay);

      const overlay = videoBackground.querySelector('[class*="text-inside"]');
      bigPlay.addEventListener('click', () => {
        video.muted = false;
        video.controls = true;
        video.play();
        bigPlay.hidden = true;
        if (overlay) overlay.hidden = true;
      });
      video.addEventListener('pause', () => {
        if (!video.controls) bigPlay.hidden = false;
      });
    };

    const existingVideo = player.querySelector('video');
    if (existingVideo) {
      setup(existingVideo);
      return;
    }

    const observer = new MutationObserver(() => {
      const video = player.querySelector('video');
      if (video) {
        observer.disconnect();
        setup(video);
      }
    });
    observer.observe(player, { childList: true });
  }

  function initAllGalleries() {
    document.querySelectorAll('.rr-product-main').forEach(initGalleryChrome);
    document.querySelectorAll('.rr-values--carousel').forEach(initValuesSlider);
    document.querySelectorAll('[data-section-type="featured-video"]').forEach(initHeroVideoSound);
  }

  document.addEventListener('DOMContentLoaded', initAllGalleries);
  if (document.readyState !== 'loading') initAllGalleries();
  document.addEventListener('theme:product-info:loaded', (event) => {
    const root = event.target.closest && event.target.closest('.rr-product-main');
    if (root) initGalleryChrome(root);
  });

  // Figma shows breadcrumbs as a full-width row above BOTH the gallery and form columns. The
  // theme's own breadcrumb output (enabled via the title block's "Navigation" setting) renders
  // nested inside the form column instead, right above the <h1> — CSS grid-column can only
  // reposition a DIRECT child of the grid, so to span full width we move the <nav> itself to be
  // the first child of .product-single__wrapper (the 2-column grid).
  function moveBreadcrumbFullWidth(root) {
    const nav = root.querySelector('nav.breadcrumbs');
    const wrapper = root.querySelector('.product-single__wrapper');
    if (!nav || !wrapper || nav.dataset.rrMoved) return;
    wrapper.insertAdjacentElement('afterbegin', nav);
    nav.dataset.rrMoved = 'true';
  }
  function moveAllBreadcrumbs() {
    document.querySelectorAll('.rr-product-main').forEach(moveBreadcrumbFullWidth);
  }
  document.addEventListener('DOMContentLoaded', moveAllBreadcrumbs);
  if (document.readyState !== 'loading') moveAllBreadcrumbs();
  document.addEventListener('theme:product-info:loaded', (event) => {
    const root = event.target.closest && event.target.closest('.rr-product-main');
    if (root) moveBreadcrumbFullWidth(root);
  });

  // Quantity + Add to Cart sitting in one row (Figma) used to be built by JS-moving the Add to
  // Cart button out of .product__submit__buttons into a wrapper next to quantity. That broke a
  // real integration: Selleasy (a "frequently bought together" app, injected via its own script)
  // looks specifically for .product__submit__buttons on the MAIN product's form and appends its
  // bundle-discount widget there, right next to Add to Cart. With the button physically removed
  // from that container, Selleasy couldn't find what it expected and silently fell back to
  // attaching inside the unrelated complementary-product card instead — moving the DOM node was
  // the actual root cause, confirmed by testing the untouched product.rogue-redesign template
  // (shares this same JS file) and the true published theme (no such move at all, correct
  // placement). The row is now pure CSS (see .product__form grid-template-areas in
  // rr-product-redesign.css) — nothing in this file relocates the button or quantity wrapper, so
  // every third-party script still finds the DOM exactly where the theme originally put it.

  // Size labels are long descriptive strings ("S - Fits Neck Circumference 25cm - 33cm | Width
  // 3.5cm | Length 45cm") rather than Figma's bare "S"/"M"/"L". We shorten the VISIBLE button
  // text to just the leading token, split on the FIRST bare "-" (not " - " — some products have
  // inconsistent/missing spacing around the dash, e.g. "S-Fits Neck..." with no spaces at all,
  // which a " - " split silently fails to shorten at all), keep the full string as a native
  // hover tooltip (title/aria-label), and stash everything after that first dash as a per-size
  // "note" rendered below the button row for the CURRENTLY SELECTED size only, updating on
  // change. A label with nothing to split (no dash at all) has no note and nothing renders.
  function isSizeFieldset(fieldset) {
    if (fieldset.classList.contains('radio__fieldset--sizeguide')) return true;
    const legend = fieldset.querySelector('legend');
    const name = (legend ? legend.textContent : '').trim().toLowerCase();
    return /\bsizes?\b/.test(name);
  }

  function initSizeFieldset(fieldset) {
    if (fieldset.dataset.rrSizeInit) return;
    fieldset.dataset.rrSizeInit = 'true';

    fieldset.querySelectorAll('.radio__input').forEach((input) => {
      const label = fieldset.querySelector('label[for="' + input.id + '"]');
      if (!label) return;
      const full = (input.value || label.textContent).trim();
      const dashIndex = full.indexOf('-');
      const short = dashIndex === -1 ? full : full.slice(0, dashIndex).trim();
      const note = dashIndex === -1 ? '' : full.slice(dashIndex + 1).trim();
      input.dataset.rrSizeNote = note;
      label.setAttribute('title', full);
      label.setAttribute('aria-label', full);
      if (short) label.textContent = short;
    });

    const note = document.createElement('div');
    note.className = 'rr-size-note';
    note.hidden = true;
    fieldset.insertAdjacentElement('beforeend', note);

    let guide = fieldset.querySelector('.product__popup__link');
    if (!guide) {
      const holder = document.querySelector('.rr-product-main .product__form__size-chart');
      const external = holder && holder.querySelector('.product__popup__link');
      const legend = fieldset.querySelector('.radio__legend') || fieldset.querySelector('legend');
      if (external && legend) {
        legend.appendChild(external);
        holder.hidden = true;
        guide = external;
      }
    }

    function syncNote() {
      const checked = fieldset.querySelector('.radio__input:checked');
      const text = (checked && checked.dataset.rrSizeNote) || '';
      note.textContent = '';
      if (text) {
        note.appendChild(document.createTextNode(text.replace(/[.\s]+$/, '') + '. '));
        if (guide) {
          note.appendChild(document.createTextNode('Not sure? '));
          const link = document.createElement('button');
          link.type = 'button';
          link.className = 'rr-size-note__link';
          link.textContent = 'Use the size guide';
          link.addEventListener('click', () => guide.click());
          note.appendChild(link);
        }
      }
      note.hidden = !text;
    }

    fieldset.addEventListener('change', (event) => {
      if (event.target.matches('.radio__input')) syncNote();
    });
    syncNote();
  }

  function initSizeLabels(root) {
    root.querySelectorAll('.radio__fieldset').forEach((fieldset) => {
      if (isSizeFieldset(fieldset)) initSizeFieldset(fieldset);
    });
  }

  function syncStock(root) {
    const countdown = root.querySelector('.variant__countdown');
    if (!countdown) return;
    const out = countdown.classList.contains('count-is-out');
    root.classList.toggle('rr-oos', out);
    // The sticky bar lives outside .rr-product-main, so it needs its own flag.
    document.querySelectorAll('.cart-bar').forEach((bar) => bar.classList.toggle('rr-oos', out));
  }

  // The theme toggles count-is-out on .variant__countdown from its own inventory script, which
  // runs after every variant swap and does not wait for our variantChange handler. Watching the
  // class attribute is the only signal that stays correct whichever order the two run in.
  function watchStock(root) {
    if (root.dataset.rrStockInit) return;
    root.dataset.rrStockInit = 'true';
    syncStock(root);
    new MutationObserver(() => syncStock(root)).observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  function initAllSizeLabels() {
    document.querySelectorAll('.rr-product-main').forEach((root) => {
      initSizeLabels(root);
      watchStock(root);
    });
  }
  document.addEventListener('DOMContentLoaded', initAllSizeLabels);
  if (document.readyState !== 'loading') initAllSizeLabels();
  document.addEventListener('theme:product-info:loaded', (event) => {
    const root = event.target.closest && event.target.closest('.rr-product-main');
    if (root) initSizeLabels(root);
  });
  // Picking a size/style re-runs product-info.js's own AJAX flow (handleOptionValueChange in
  // product-info.js), which swaps in a freshly server-rendered <variant-selects> — a brand new
  // .radio__fieldset--sizeguide with none of our shortening/note-building applied, and none of
  // our dataset flags (so initSizeLabels's own guard doesn't block re-running on it). That swap
  // does NOT redispatch theme:product-info:loaded (that only fires once, from connectedCallback),
  // but the theme does publish its own PUB_SUB_EVENTS.variantChange after every such swap — the
  // theme's own cart-bar.js subscribes to it the same way, so we do too.
  (function bindVariantChangeReinit(attemptsLeft) {
    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS && window.theme.PUB_SUB_EVENTS.variantChange) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, initAllSizeLabels);
      return;
    }
    if (attemptsLeft > 0) requestAnimationFrame(() => bindVariantChangeReinit(attemptsLeft - 1));
  })(60);

  document.addEventListener('click', (event) => {
    const accordionButton = event.target.closest('[data-rr-accordion-button]');
    if (accordionButton) {
      const panel = document.getElementById(accordionButton.getAttribute('aria-controls'));
      if (!panel) return;
      const expanded = accordionButton.getAttribute('aria-expanded') === 'true';
      accordionButton.setAttribute('aria-expanded', String(!expanded));
      panel.classList.toggle('is-open', !expanded);
      return;
    }

    const descriptionButton = event.target.closest('[data-rr-description-toggle]');
    if (descriptionButton) {
      const description = descriptionButton.closest('.rr-description');
      const expanded = descriptionButton.getAttribute('aria-expanded') === 'true';
      descriptionButton.setAttribute('aria-expanded', String(!expanded));
      descriptionButton.textContent = expanded ? 'Read More' : 'Read Less';
      description.classList.toggle('is-open', !expanded);
      return;
    }

    const pauseButton = event.target.closest('[data-rr-marquee-pause]');
    if (pauseButton) {
      const strip = pauseButton.closest('.rr-strip');
      const paused = strip.classList.toggle('is-paused');
      pauseButton.setAttribute('aria-pressed', String(paused));
      pauseButton.textContent = paused ? 'Play' : 'Pause';
    }
  });
})();
